"use client";

import { PropsWithChildren, useEffect } from "react";
import { usePathname } from "next/navigation";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSessionContext } from "@/context/SessionContext";
import { LoadingSpinner, SidebarProvider, SidebarTrigger } from "@focal/ui-kit";
import {
  AppSidebar,
  IntegrationReconnectBanner,
  BillingStatusBanner,
} from "@/components/common";
import { AskFocalSidebar } from "@/components/common/AskFocalSidebar";
import {
  clampChatWidth,
  DEFAULT_PANEL_WIDTH,
  useAskFocalSidebarStore,
} from "@/store/askFocalSidebar";
import { useScreenWidth } from "@/hooks/useWindowSize";
import { cn } from "@/lib/utils";

export const MainLayout = ({ children }: PropsWithChildren) => {
  const { isReady, isFeatureFlagFetched } = useSessionContext();
  if (!isReady || !isFeatureFlagFetched) {
    return (
      <div className="flex flex-1 justify-center items-center w-full h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <MainLayoutInner>{children}</MainLayoutInner>
    </SidebarProvider>
  );
};

const MainLayoutInner = ({ children }: PropsWithChildren) => {
  const askFocalOpen = useAskFocalSidebarStore((s) => s.isOpen);
  const slideIn = useAskFocalSidebarStore((s) => s.slideIn);
  const isDragging = useAskFocalSidebarStore((s) => s.isDragging);
  const panelWidth = useAskFocalSidebarStore((s) => s.panelWidth);
  const { isMobile } = useIsMobile();
  const { width: screenWidth } = useScreenWidth();
  const pathname = usePathname();
  // Conversation details page renders its own inline sidebar toggle next to
  // the title when the new header is active. Hide the floating one to avoid
  // duplicates.
  const hideFloatingTrigger = /^\/conversations\/[^/]+$/.test(pathname ?? "");

  // AskFocal is page-scoped — leaving the page must close the sidebar and
  // drop the threadKey so the next open starts in the new page's context.
  // Clearing threadKey also unmounts AskFocalV2, which wipes the in-memory
  // history bucket via its own cleanup effect. Also reset `slideIn` so the
  // push-mode padding collapses to 0 on the new page (it's keyed off
  // `slideIn`, not `isOpen`), and reset `panelWidth` to default so each new
  // page opens the chat at a predictable width regardless of any resize the
  // user did on the previous page.
  useEffect(() => {
    useAskFocalSidebarStore.setState({
      isOpen: false,
      threadKey: null,
      scope: {},
      slideIn: false,
      isDragging: false,
      panelWidth: DEFAULT_PANEL_WIDTH,
    });
  }, [pathname]);

  // Push-mode geometry: at lg+ (≥1024px) reserve the chat's edge space via
  // padding-right on <main>. Keyed off `slideIn` (not `isOpen`) so the
  // initial open paints with paddingRight=0 and the next rAF flips it →
  // CSS transition animates 0 → width. Symmetric on close.
  //
  // On the conversation-details page, ConversationLayoutContext takes
  // over and drives padding-right via JS rAF (in lockstep with Flows
  // width and chat panel transform). On other pages this CSS transition
  // handles the animation by itself.
  const paddingRight =
    screenWidth >= 1024 && slideIn ? clampChatWidth(panelWidth) : 0;

  return (
    <>
      <AppSidebar className="z-20" />
      <main
        className={cn(
          "flex flex-1 w-full min-w-0 flex-col relative pt-4 lg:pt-0 z-10 h-screen min-h-0 isolate",
          !isDragging &&
            "transition-[padding-right] duration-motion-default ease-emphasized",
        )}
        style={{ paddingRight }}
      >
        {!(askFocalOpen && !isMobile) && !hideFloatingTrigger && (
          <SidebarTrigger
            className="absolute top-3.5 left-2 z-20 lg:hidden"
            size="icon"
            variant="ghost"
          />
        )}
        <BillingStatusBanner />
        <IntegrationReconnectBanner />
        {children}
      </main>
      <AskFocalSidebar />
    </>
  );
};
